import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isAppleClientConfigured, startAppleSignIn } from '../lib/appleAuth';
import { consumeGoogleRedirect, isGoogleClientConfigured, startGoogleSignIn } from '../lib/googleAuth';
import './Login.css';

const AUTH_REDIRECT_KEY = 'laurenti-auth-redirect';

function normalizePhone(value) {
  const compact = String(value || '').replace(/[\s()-]/g, '');
  if (!/^\+[1-9]\d{7,14}$/.test(compact)) {
    return '';
  }
  return compact;
}

function Login() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [method, setMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [oauthBusy, setOauthBusy] = useState('');
  const {
    signIn,
    signUp,
    signInWithGoogleIdToken,
    signInWithAppleIdToken,
    signInWithPhone,
    verifyPhoneOtp,
    user,
    loading,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const storedRedirect =
    typeof window !== 'undefined' ? sessionStorage.getItem(AUTH_REDIRECT_KEY) : null;
  const redirectTo = location.state?.from || storedRedirect || '/';

  useEffect(() => {
    if (user) {
      sessionStorage.removeItem(AUTH_REDIRECT_KEY);
    }
  }, [user]);

  useEffect(() => {
    const redirected = consumeGoogleRedirect();
    if (!redirected) {
      return undefined;
    }

    if (redirected.error || !redirected.idToken) {
      setError('Google sign-in was cancelled.');
      return undefined;
    }

    let active = true;
    setOauthBusy('google');

    signInWithGoogleIdToken(redirected.idToken, redirected.nonce).then(({ error: authError }) => {
      if (!active) return;
      setOauthBusy('');
      if (authError) {
        setError(authError.message);
      }
    });

    return () => {
      active = false;
    };
    // Only handle a Google redirect once, when this page loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div className="login-loading">Loading…</div>;
  }

  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  const rememberRedirect = () => {
    if (redirectTo && redirectTo !== '/') {
      sessionStorage.setItem(AUTH_REDIRECT_KEY, redirectTo);
    }
  };

  const resetAlerts = () => {
    setError('');
    setMessage('');
  };

  const handleEmailSubmit = async event => {
    event.preventDefault();
    resetAlerts();
    setSubmitting(true);

    const { error: authError } = isSignUp
      ? await signUp(email, password)
      : await signIn(email, password);

    setSubmitting(false);

    if (authError) {
      setError(authError.message);
      return;
    }

    if (isSignUp) {
      setMessage('Account created. Confirm your email, then sign in.');
      setIsSignUp(false);
      return;
    }

    sessionStorage.removeItem(AUTH_REDIRECT_KEY);
    navigate(redirectTo, { replace: true });
  };

  const handleApple = async () => {
    resetAlerts();
    setOauthBusy('apple');
    rememberRedirect();

    if (!isAppleClientConfigured()) {
      setOauthBusy('');
      setError('Apple sign-in is not configured.');
      return;
    }

    try {
      const result = await startAppleSignIn();
      const { error: authError } = await signInWithAppleIdToken(
        result.token,
        result.nonce,
        result.user
      );
      if (authError) {
        setError(authError.message);
      }
    } catch (authError) {
      const cancelled =
        authError?.error === 'popup_closed_by_user' ||
        String(authError?.message || '').toLowerCase().includes('popup');
      setError(cancelled ? 'Apple sign-in was cancelled.' : authError.message || 'Apple sign-in failed.');
    } finally {
      setOauthBusy('');
    }
  };

  const handleGoogle = async () => {
    resetAlerts();
    setOauthBusy('google');
    rememberRedirect();

    if (!isGoogleClientConfigured()) {
      setOauthBusy('');
      setError('Google sign-in is not configured.');
      return;
    }

    try {
      await startGoogleSignIn();
    } catch (authError) {
      setOauthBusy('');
      setError(authError.message || 'Google sign-in failed.');
    }
  };

  const handlePhoneSubmit = async event => {
    event.preventDefault();
    resetAlerts();

    const normalized = normalizePhone(phone);
    if (!normalized) {
      setError('Enter a phone number with country code, for example +44 7700 900123.');
      return;
    }

    setSubmitting(true);

    if (otpSent) {
      const { error: authError } = await verifyPhoneOtp(normalized, otp.trim());
      setSubmitting(false);

      if (authError) {
        setError(authError.message);
        return;
      }

      sessionStorage.removeItem(AUTH_REDIRECT_KEY);
      navigate(redirectTo, { replace: true });
      return;
    }

    const { error: authError } = await signInWithPhone(normalized);
    setSubmitting(false);

    if (authError) {
      setError(
        authError.message.includes('Unsupported phone provider') ||
        (authError.message.includes('phone') && authError.message.includes('disabled'))
          ? 'Phone sign-in is not available yet. Please use email.'
          : authError.message
      );
      return;
    }

    setOtpSent(true);
    setMessage('We sent a 6-digit code to your phone.');
  };

  const showPhone = method === 'phone';
  const heading = showPhone ? 'Phone sign in' : isSignUp ? 'Create account' : 'Sign in';
  const lede = showPhone
    ? otpSent
      ? 'Enter the code we sent by SMS.'
      : 'We will text you a one-time code. No password needed.'
    : isSignUp
      ? 'Join Laurenti Apparel to checkout and track orders.'
      : 'Sign in to checkout and view your orders.';

  return (
    <div className="login-page">
      <div className="login-page-inner">
        <p className="store-eyebrow">Account</p>
        <h1 className="store-title">{heading}</h1>
        <p className="store-lede">{lede}</p>

        {!showPhone && (
          <div className="login-oauth">
            <button
              type="button"
              className="login-oauth-btn"
              onClick={handleApple}
              disabled={Boolean(oauthBusy) || submitting}
            >
              <AppleIcon />
              {oauthBusy === 'apple' ? 'Opening Apple…' : 'Continue with Apple'}
            </button>
            <button
              type="button"
              className="login-oauth-btn"
              onClick={handleGoogle}
              disabled={Boolean(oauthBusy) || submitting}
            >
              <GoogleIcon />
              {oauthBusy === 'google' ? 'Redirecting…' : 'Continue with Google'}
            </button>
            <button
              type="button"
              className="login-oauth-btn"
              onClick={() => {
                resetAlerts();
                setMethod('phone');
                setOtp('');
                setOtpSent(false);
              }}
              disabled={Boolean(oauthBusy) || submitting}
            >
              <PhoneIcon />
              Continue with phone
            </button>
          </div>
        )}

        {!showPhone && <p className="login-divider"><span>or</span></p>}

        {showPhone ? (
          <form className="login-form" onSubmit={handlePhoneSubmit}>
            <label className="store-field">
              Phone number
              <input
                className="store-input"
                type="tel"
                value={phone}
                onChange={event => setPhone(event.target.value)}
                required
                autoComplete="tel"
                placeholder="+44 7700 900123"
                disabled={otpSent}
              />
            </label>

            {otpSent && (
              <label className="store-field">
                Code
                <input
                  className="store-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={event => setOtp(event.target.value)}
                  required
                  minLength={6}
                  maxLength={8}
                  placeholder="123456"
                />
              </label>
            )}

            {error && <p className="store-error">{error}</p>}
            {message && <p className="store-status">{message}</p>}

            <button type="submit" className="store-btn store-btn--block" disabled={submitting}>
              {submitting ? 'Please wait…' : otpSent ? 'Verify code' : 'Send code'}
            </button>

            {otpSent && (
              <button
                type="button"
                className="login-text-btn"
                onClick={() => {
                  setOtp('');
                  setOtpSent(false);
                  setMessage('');
                  setError('');
                }}
              >
                Use a different number
              </button>
            )}
          </form>
        ) : (
          <form className="login-form" onSubmit={handleEmailSubmit}>
            <label className="store-field">
              Email
              <input
                className="store-input"
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                required
                autoComplete="email"
              />
            </label>

            <label className="store-field">
              Password
              <input
                className="store-input"
                type="password"
                value={password}
                onChange={event => setPassword(event.target.value)}
                required
                minLength={6}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
              />
            </label>

            {error && <p className="store-error">{error}</p>}
            {message && <p className="store-status">{message}</p>}

            <button type="submit" className="store-btn store-btn--block" disabled={submitting}>
              {submitting ? 'Please wait…' : isSignUp ? 'Create account' : 'Sign in'}
            </button>
          </form>
        )}

        <p className="login-switch">
          {showPhone ? (
            <button
              type="button"
              onClick={() => {
                setMethod('email');
                setOtp('');
                setOtpSent(false);
                resetAlerts();
              }}
            >
              Use email instead
            </button>
          ) : (
            <>
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  resetAlerts();
                }}
              >
                {isSignUp ? 'Sign in' : 'Create one'}
              </button>
            </>
          )}
        </p>

        <Link to="/" className="login-home">
          Back to home
        </Link>
      </div>
    </div>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16.37 12.63c.03 3.16 2.77 4.21 2.8 4.23-.02.07-.44 1.5-1.45 2.97-.87 1.27-1.78 2.53-3.21 2.56-1.4.03-1.85-.83-3.45-.83s-2.1.8-3.42.86c-1.37.06-2.42-1.37-3.3-2.63-1.8-2.58-3.18-7.3-1.33-10.48.92-1.58 2.56-2.58 4.34-2.61 1.35-.03 2.63.91 3.45.91s2.28-1.12 3.85-.96c.66.03 2.5.26 3.68 2.01-.1.06-2.2 1.28-2.16 3.97ZM14.2 6.3c.73-.88 1.22-2.11 1.09-3.33-1.05.04-2.32.7-3.07 1.58-.68.78-1.27 2.04-1.11 3.24 1.17.09 2.37-.6 3.09-1.49Z"
      />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M21.6 12.23c0-.74-.07-1.45-.19-2.13H12v4.03h5.38a4.6 4.6 0 0 1-2 3.02v2.5h3.24c1.9-1.75 2.98-4.33 2.98-7.42Z"
      />
      <path
        fill="currentColor"
        d="M12 22c2.7 0 4.97-.9 6.63-2.35l-3.24-2.5c-.9.6-2.05.96-3.39.96-2.6 0-4.81-1.76-5.6-4.12H3.06v2.58A9.99 9.99 0 0 0 12 22Z"
        opacity=".85"
      />
      <path
        fill="currentColor"
        d="M6.4 13.99A6 6 0 0 1 6.08 12c0-.69.12-1.36.32-1.99V7.43H3.06A9.99 9.99 0 0 0 2 12c0 1.61.39 3.14 1.06 4.57l3.34-2.58Z"
        opacity=".7"
      />
      <path
        fill="currentColor"
        d="M12 5.88c1.47 0 2.79.5 3.83 1.5l2.87-2.87C16.96 2.86 14.7 2 12 2A9.99 9.99 0 0 0 3.06 7.43l3.34 2.58C7.19 7.64 9.4 5.88 12 5.88Z"
        opacity=".55"
      />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M8.2 3.5h7.6c.94 0 1.7.76 1.7 1.7v13.6c0 .94-.76 1.7-1.7 1.7H8.2c-.94 0-1.7-.76-1.7-1.7V5.2c0-.94.76-1.7 1.7-1.7Zm.3 2.2v10.6h7v-10.6h-7Zm3.5 12.2c.5 0 .9.4.9.9s-.4.9-.9.9-.9-.4-.9-.9.4-.9.9-.9Z"
      />
    </svg>
  );
}

export default Login;
